import { useState, useEffect, useCallback } from 'react';
import {
  apiDownloadOrderReport,
  apiGetAllOrders,
  apiUpdateOrderStatus,
  apiUpdateReturnStatus,
} from '../api';
import Pagination from '../components/Pagination';
import OrdersTable from './orders/OrdersTable';
import OrderDetailDrawer from './orders/OrderDetailDrawer';
import { statusOptions, formatStatusLabel } from './orders/ordersConstants';
import { FiSearch, FiX } from 'react-icons/fi';

const OrdersManager = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, pages: 1 });
  const [filterStatus, setFilterStatus] = useState('');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [reportDays, setReportDays] = useState('7');
  const [reportDownloading, setReportDownloading] = useState(false);

  const loadOrders = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiGetAllOrders(page, 15, filterStatus, debouncedSearch);
      setOrders(data.orders || []);
      setPagination(data.pagination || { total: 0, pages: 1 });
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [page, filterStatus, debouncedSearch]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(1);
    }, 350);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  const handleStatusChange = async (orderId, newStatus, shippingDetails) => {
    try {
      const { order: updatedOrder } = await apiUpdateOrderStatus(
        orderId,
        newStatus,
        shippingDetails
      );
      setOrders((current) => current.map((order) => (
        (order.orderId || order.id) === orderId ? { ...order, ...updatedOrder } : order
      )));
      setSelectedOrder((current) => (
        current && (current.orderId || current.id) === orderId
          ? { ...current, ...updatedOrder }
          : current
      ));
      return updatedOrder;
    } catch { /* API client displays the error toast. */ }
    return null;
  };

  const handleReturnSave = async (returnId, newStatus, adminNote, refundAmount, shippingRefundAmount, otherRefundAmount) => {
    try {
      const { returnRequest } = await apiUpdateReturnStatus(returnId, newStatus, adminNote, refundAmount, shippingRefundAmount, otherRefundAmount);
      const updateItems = (items) =>
        (items || []).map((it) =>
          it.returnRequest && it.returnRequest.id === returnId
            ? {
                ...it,
                returnRequest: {
                  ...it.returnRequest,
                  status: returnRequest.status,
                  adminNote: returnRequest.adminNote,
                  resolvedAt: returnRequest.resolvedAt,
                  refundAmount: returnRequest.refundAmount,
                  shippingRefundAmount: returnRequest.shippingRefundAmount,
                  otherRefundAmount: returnRequest.otherRefundAmount,
                },
              }
            : it
        );
      setOrders((prev) => prev.map((o) => ({ ...o, items: updateItems(o.items) })));
      if (selectedOrder) {
        setSelectedOrder({ ...selectedOrder, items: updateItems(selectedOrder.items) });
      }
    } catch (err) {
      throw err;
    }
  };

  const handleReportDownload = async () => {
    if (reportDownloading) return;
    setReportDownloading(true);
    try {
      await apiDownloadOrderReport(reportDays, filterStatus);
    } catch {
      // API client displays the error toast.
    } finally {
      setReportDownloading(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <label className="relative min-w-[260px] flex-1 sm:max-w-md">
          <FiSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          <input
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search order, customer, item or tracking…"
            aria-label="Search orders"
            className="w-full rounded-lg border border-gray-300 py-2 pl-10 pr-10 text-sm focus:border-orange-500 focus:outline-none focus:ring-1 focus:ring-orange-500"
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch('')}
              aria-label="Clear order search"
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-black"
            >
              <FiX />
            </button>
          )}
        </label>
        <select
          value={filterStatus}
          onChange={(e) => {
            setFilterStatus(e.target.value);
            setPage(1);
          }}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-orange-500"
        >
          <option value="">All Status</option>
          {statusOptions.map((s) => (
            <option key={s} value={s}>
              {formatStatusLabel(s)}
            </option>
          ))}
        </select>
        <span className="text-sm text-black">{pagination.total} orders total</span>
        <div className="flex flex-wrap items-center gap-2 sm:ml-auto">
          <label htmlFor="order-report-range" className="text-sm font-medium text-gray-700">
            Order Report
          </label>
          <select
            id="order-report-range"
            value={reportDays}
            onChange={(e) => setReportDays(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-orange-500"
          >
            <option value="1">Date range: Last day</option>
            <option value="2">Date range: Last 2 days</option>
            <option value="7">Date range: Last 7 days</option>
            <option value="15">Date range: Last 15 days</option>
            <option value="30">Date range: Last 30 days</option>
            <option value="60">Date range: Last 60 days</option>
          </select>
          <button
            type="button"
            onClick={handleReportDownload}
            disabled={reportDownloading}
            className="rounded-lg bg-orange-500 px-4 py-2 text-sm font-medium text-white hover:bg-orange-600 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {reportDownloading ? 'Downloading…' : 'Download report'}
          </button>
        </div>
      </div>

      <OrdersTable
        orders={orders}
        loading={loading}
        selectedOrder={selectedOrder}
        onSelect={setSelectedOrder}
        onStatusChange={handleStatusChange}
      />

      <Pagination page={page} pages={pagination.pages} onChange={setPage} />

      <OrderDetailDrawer
        order={selectedOrder}
        onClose={() => setSelectedOrder(null)}
        onReturnSave={handleReturnSave}
        onStatusChange={handleStatusChange}
      />
    </div>
  );
};

export default OrdersManager;
